import { adToken, adsAllowed, type AdSignals } from './ad-consent.ts';
/**
 * First-touch marketing source, as the marketing site forwards it.
 *
 * The site and the app keep this in a 90-day `bc_src` cookie
 * (`public/bc-attribution.js`) and send it two ways: an `attribution` object
 * on the trial-signup and demo-session JSON, and the same facts as query
 * params on the OAuth start URL (`utm_source`, `utm_medium`, `utm_campaign`,
 * `utm_term`, `utm_content`, `referrer`, `landing_path`). See the website's
 * `src/shared/traffic-source.ts`.
 *
 * Nothing here may refuse an account. A missing, oversized, or nonsensical
 * value is dropped or clipped; the signup still happens. Unknown keys are
 * ignored — the site and the app deploy separately, and a field this app has
 * not heard of yet must not 400 a trial.
 */

export const TRAFFIC_SOURCE_LIMITS = {
  referrer: 512,
  landingPath: 512,
  utm: 128,
  /** Partner and referral identifiers. Over-long values are dropped, not clipped. */
  affiliate: 64,
  campaign: 128,
} as const;

/**
 * The label the operator list uses for an account that arrived with no
 * `utm_source`. Not a value a campaign is expected to send.
 */
export const SIGNUP_SOURCE_NONE = '(none)';

export interface TrafficSource {
  referrer: string | null;
  landingPath: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
}

/**
 * Marketing source plus who should be credited for the signup. The credit
 * fields come from the marketing site's HttpOnly referral cookies, not from
 * the `bc_src` cookie, and they travel on the same `attribution` object.
 */
export interface SignupAttribution extends TrafficSource, Partial<AdSignals> {
  visitorId?: string | null;
  partnerSlug?: string | null;
  referralCode?: string | null;
  campaign?: string | null;
}

function capped(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

/**
 * A credit identifier that is too long is discarded rather than clipped.
 * Clipping could turn an unknown code into a shorter one that really belongs
 * to somebody, and these fields decide who gets paid.
 */
function affiliateToken(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > max) return null;
  return trimmed;
}

/** Camel-case wins when both spellings are present and the first one has text. */
function firstText(primary: unknown, fallback: unknown): unknown {
  if (typeof primary === 'string' && primary.trim()) return primary;
  return fallback;
}

export function presentTrafficSource(source: TrafficSource | null | undefined): TrafficSource | null {
  if (!source) return null;
  if (
    !source.referrer &&
    !source.landingPath &&
    !source.utmSource &&
    !source.utmMedium &&
    !source.utmCampaign &&
    !source.utmTerm &&
    !source.utmContent
  ) {
    return null;
  }
  return source;
}

/**
 * Dropped in this order when a signed cookie cannot hold the whole source.
 * The operator list counts `utmSource`, so that is the last field to go.
 */
const TRAFFIC_SOURCE_DROP_ORDER = [
  'referrer',
  'landingPath',
  'utmContent',
  'utmTerm',
  'utmCampaign',
  'utmMedium',
  'utmSource',
] as const;

/**
 * The largest prefix of `source` for which `fits` is true.
 *
 * `fits(null)` means "the cookie without a source". A source that cannot be
 * made to fit is dropped entirely — losing the channel is acceptable, losing
 * the signup is not.
 */
export function shrinkTrafficSourceSync(
  source: TrafficSource,
  fits: (candidate: TrafficSource | null) => boolean,
): TrafficSource | null {
  if (fits(source)) return source;
  let current: TrafficSource = { ...source };
  for (const field of TRAFFIC_SOURCE_DROP_ORDER) {
    if (!current[field]) continue;
    current = { ...current, [field]: null };
    const present = presentTrafficSource(current);
    if (fits(present)) return present;
  }
  return null;
}

export async function shrinkTrafficSource(
  source: TrafficSource,
  fits: (candidate: TrafficSource | null) => boolean | Promise<boolean>,
): Promise<TrafficSource | null> {
  if (await fits(source)) return source;
  let current: TrafficSource = { ...source };
  for (const field of TRAFFIC_SOURCE_DROP_ORDER) {
    if (!current[field]) continue;
    current = { ...current, [field]: null };
    const present = presentTrafficSource(current);
    if (await fits(present)) return present;
  }
  return null;
}

/**
 * The marketing fields of an attribution payload. Null when there is nothing
 * worth storing — including when the caller sent a string, an array, or only
 * keys this app does not keep.
 */
export function sanitizeTrafficSource(value: unknown): TrafficSource | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  return presentTrafficSource({
    referrer: capped(record.referrer, TRAFFIC_SOURCE_LIMITS.referrer),
    landingPath: capped(firstText(record.landingPath, record.landing_path), TRAFFIC_SOURCE_LIMITS.landingPath),
    utmSource: capped(firstText(record.utmSource, record.utm_source), TRAFFIC_SOURCE_LIMITS.utm),
    utmMedium: capped(firstText(record.utmMedium, record.utm_medium), TRAFFIC_SOURCE_LIMITS.utm),
    utmCampaign: capped(firstText(record.utmCampaign, record.utm_campaign), TRAFFIC_SOURCE_LIMITS.utm),
    utmTerm: capped(firstText(record.utmTerm, record.utm_term), TRAFFIC_SOURCE_LIMITS.utm),
    utmContent: capped(firstText(record.utmContent, record.utm_content), TRAFFIC_SOURCE_LIMITS.utm),
  });
}

/**
 * The whole attribution object, marketing fields and referral credit. Undefined
 * when the value is missing or useless, so a signup can proceed without one.
 */
export function sanitizeSignupAttribution(value: unknown): SignupAttribution | undefined {
  if (value == null) return undefined;
  const traffic = sanitizeTrafficSource(value);
  if (typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const attribution: SignupAttribution = {
    referrer: traffic?.referrer ?? null,
    landingPath: traffic?.landingPath ?? null,
    utmSource: traffic?.utmSource ?? null,
    utmMedium: traffic?.utmMedium ?? null,
    utmCampaign: traffic?.utmCampaign ?? null,
    utmTerm: traffic?.utmTerm ?? null,
    utmContent: traffic?.utmContent ?? null,
    adConsent: ['granted', 'not_required', 'denied', 'gpc', 'unknown'].includes(String(record.adConsent))
      ? (record.adConsent as AdSignals['adConsent'])
      : undefined,
    ...Object.fromEntries(
      ['gclid', 'gbraid', 'wbraid', 'fbclid', 'fbp', 'fbc'].map((key) => [
        key,
        adsAllowed(record.adConsent) ? adToken(record[key], key === 'fbp' ? 128 : key === 'fbc' ? 600 : 512) : null,
      ]),
    ),
    clientUserAgent:
      adsAllowed(record.adConsent) &&
      typeof record.clientUserAgent === 'string' &&
      !record.clientUserAgent.includes('@')
        ? record.clientUserAgent.slice(0, 512)
        : null,
    clientIp:
      adsAllowed(record.adConsent) &&
      typeof record.clientIp === 'string' &&
      /^[0-9a-fA-F:.]{3,45}$/.test(record.clientIp)
        ? record.clientIp
        : null,
    visitorId: affiliateToken(record.visitorId, TRAFFIC_SOURCE_LIMITS.affiliate),
    partnerSlug: affiliateToken(record.partnerSlug, TRAFFIC_SOURCE_LIMITS.affiliate),
    referralCode: affiliateToken(record.referralCode, TRAFFIC_SOURCE_LIMITS.affiliate),
    campaign: affiliateToken(record.campaign, TRAFFIC_SOURCE_LIMITS.campaign),
  };
  const hasCredit = Boolean(
    attribution.visitorId || attribution.partnerSlug || attribution.referralCode || attribution.campaign,
  );
  if (!traffic && !hasCredit && !attribution.adConsent) return undefined;
  return attribution;
}

/** The query params the marketing site appends to an OAuth start URL. */
export function trafficSourceFromSearchParams(params: { get(name: string): string | null }): TrafficSource | null {
  return sanitizeTrafficSource({
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    referrer: params.get('referrer'),
    landing_path: params.get('landing_path'),
  });
}
